import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BookOpenText,
  Bot,
  CheckCircle2,
  ChevronRight,
  ShieldCheck,
  SlidersHorizontal,
  TimerReset
} from 'lucide-react';
import { FaTelegramPlane } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { landingCopy, landingLinks } from '../components/landing/landingContent';
import {
  LandingActionLink,
  LandingBackdrop,
  LandingFooter,
  LandingNavbar,
  SocialLinks
} from '../components/landing/LandingLayout';

const displayFont = 'Bahnschrift, "Trebuchet MS", "Segoe UI Variable Display", sans-serif';

const productIcons = {
  setup: SlidersHorizontal,
  backtest: TimerReset,
  auto: Bot
};

function Reveal({ as: Tag = 'div', className = '', delay = 0, style, children, ...props }) {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;

    if (!node) {
      return undefined;
    }

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.18, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`landing-reveal ${isVisible ? 'is-visible' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...props}
    >
      {children}
    </Tag>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.34em] text-cyan-300/80 sm:text-[14px]">
      <span className="h-px w-12 bg-gradient-to-r from-cyan-300/0 via-cyan-300/80 to-cyan-300/10" />
      <span>{children}</span>
    </div>
  );
}

function ProductCard({ icon: Icon, title, text }) {
  return (
    <article className="landing-card flex h-full min-h-[16.5rem] flex-col rounded-[28px] border border-white/10 bg-white/[0.035] p-6 sm:p-7">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-5 max-w-[14ch] text-2xl font-semibold leading-tight text-white" style={{ fontFamily: displayFont }}>
        {title}
      </h3>
      <p className="mt-3 max-w-[30ch] text-sm leading-7 text-slate-300 sm:text-[15px]">{text}</p>
    </article>
  );
}

function StepCard({ index, title, text }) {
  return (
    <article className="landing-card flex h-full min-h-[16.5rem] flex-col rounded-[28px] border border-white/10 bg-white/[0.03] p-6 sm:p-7">
      <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{index}</div>
      <h3 className="mt-4 max-w-[15ch] text-[1.85rem] font-semibold leading-[1.05] text-white sm:text-[2rem]" style={{ fontFamily: displayFont }}>
        {title}
      </h3>
      <p className="mt-3 max-w-[30ch] text-sm leading-7 text-slate-300 sm:text-[15px]">{text}</p>
    </article>
  );
}

function ResourceLink({ title, text, href, icon: Icon }) {
  return (
    <LandingActionLink
      href={href}
      className="landing-card group flex h-full min-h-[130px] items-start justify-between gap-4 rounded-[24px] border border-white/10 bg-white/[0.03] px-5 py-5"
    >
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold text-white" style={{ fontFamily: displayFont }}>
          {title}
        </div>
        <div className="mt-1 text-sm text-slate-400">{text}</div>
      </div>
      <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition-colors group-hover:border-cyan-300/30 group-hover:text-white">
        <Icon className="h-4 w-4" />
      </div>
    </LandingActionLink>
  );
}

export default function LandingPage() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'ru';
  const copy = landingCopy[locale];
  const appHref = '#top';
  const appLabel = locale === 'en' ? 'Soon' : 'Скоро';
  const heroSubtitleLines = Array.isArray(copy.hero.subtitle) ? copy.hero.subtitle : [copy.hero.subtitle];

  const resourceIcons = {
    guide: BookOpenText,
    telegram: FaTelegramPlane,
    twitter: FaXTwitter
  };

  const resourceLinks = {
    guide: landingLinks.guide,
    telegram: landingLinks.telegram,
    twitter: landingLinks.twitter
  };

  return (
    <div id="top" className="min-h-screen overflow-hidden bg-slate-950 text-white">
      <LandingBackdrop />
      <LandingNavbar copy={copy} appHref={appHref} appLabel={appLabel} />

      <main className="relative z-10">
        <section className="mt-[73px] flex min-h-[calc(100svh-73px)] items-center px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="mx-auto w-full max-w-5xl text-center">
            <Reveal delay={60}>
              <h1 className="mx-auto mt-2 max-w-4xl text-balance text-4xl font-black leading-[0.95] text-white sm:text-6xl lg:text-7xl" style={{ fontFamily: displayFont }}>
                {copy.hero.title}
              </h1>
            </Reveal>
            <Reveal delay={120}>
              <div className="mx-auto mt-6 max-w-3xl space-y-3 text-base leading-8 text-slate-300 sm:text-xl">
                {heroSubtitleLines.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={180} className="mt-8">
              <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                <LandingActionLink
                  href={appHref}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-7 py-3.5 text-sm font-semibold text-cyan-50 transition-colors hover:border-cyan-300/40 hover:bg-cyan-300/15 sm:w-auto"
                >
                  {appLabel}
                  <ChevronRight className="h-4 w-4" />
                </LandingActionLink>
                <LandingActionLink
                  href={landingLinks.guide}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-100 transition-colors hover:border-white/20 hover:text-white sm:w-auto"
                >
                  <BookOpenText className="h-4 w-4" />
                  {copy.hero.secondary}
                </LandingActionLink>
              </div>
            </Reveal>

            <Reveal delay={240} className="mt-10">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {copy.hero.points.map((item) => (
                  <div key={item} className="landing-card rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-slate-200">
                    {item}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={300} className="mt-8 flex flex-col items-center gap-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{copy.hero.socialLabel}</div>
              <SocialLinks />
            </Reveal>
          </div>
        </section>

        <section id="product" className="scroll-mt-28 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <Reveal className="max-w-3xl">
              <SectionEyebrow>{copy.product.eyebrow}</SectionEyebrow>
              <div className="mt-4">
                <h2 className="text-4xl font-black text-white sm:text-5xl" style={{ fontFamily: displayFont }}>
                  {copy.product.title}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300">{copy.product.subtitle}</p>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {copy.product.cards.map((card, index) => (
                <Reveal
                  key={card.title}
                  delay={index * 90}
                  className={index === copy.product.cards.length - 1 ? 'md:col-span-2 xl:col-span-1' : ''}
                >
                  <ProductCard icon={productIcons[card.key]} title={card.title} text={card.text} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="flow" className="scroll-mt-28 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <Reveal className="max-w-3xl">
              <SectionEyebrow>{copy.flow.eyebrow}</SectionEyebrow>
              <div className="mt-4">
                <h2 className="text-4xl font-black text-white sm:text-5xl" style={{ fontFamily: displayFont }}>
                  {copy.flow.title}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300">{copy.flow.subtitle}</p>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {copy.flow.steps.map((step, index) => (
                <Reveal
                  key={step.index}
                  delay={index * 90}
                  className={index === copy.flow.steps.length - 1 ? 'md:col-span-2 xl:col-span-1' : ''}
                >
                  <StepCard index={step.index} title={step.title} text={step.text} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="security" className="scroll-mt-28 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <Reveal className="mx-auto max-w-6xl rounded-[32px] border border-white/10 bg-white/[0.035] p-7 sm:p-8 lg:p-10">
            <div className="grid gap-8 xl:grid-cols-[1fr_0.85fr] xl:items-start">
              <div>
                <SectionEyebrow>{copy.security.eyebrow}</SectionEyebrow>
                <h2 className="mt-4 max-w-2xl text-4xl font-black text-white sm:text-5xl" style={{ fontFamily: displayFont }}>
                  {copy.security.title}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300">{copy.security.text}</p>
              </div>
              <Reveal delay={120}>
                <div className="landing-card rounded-[28px] border border-white/10 bg-slate-950/45 p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div className="mt-6 space-y-4">
                    {copy.security.bullets.map((item) => (
                      <div key={item} className="flex items-start gap-3 text-sm leading-7 text-slate-200">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-cyan-300" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 rounded-[22px] border border-white/10 bg-white/[0.04] px-4 py-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{copy.security.partnerLabel}</div>
                    <div className="mt-3 flex items-center gap-3">
                      <img src="/bybit-logo.png" alt="Bybit" className="h-6 w-auto" />
                      <span className="text-base font-semibold text-white">{copy.security.partnerText}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </Reveal>
        </section>

        <section id="guide" className="scroll-mt-28 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <Reveal className="max-w-3xl">
              <SectionEyebrow>{copy.resources.eyebrow}</SectionEyebrow>
              <div className="mt-4">
                <h2 className="text-4xl font-black text-white sm:text-5xl" style={{ fontFamily: displayFont }}>
                  {copy.resources.title}
                </h2>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {copy.resources.items.map((item, index) => (
                <Reveal
                  key={item.key}
                  delay={index * 90}
                  className={`h-full ${index === copy.resources.items.length - 1 ? 'md:col-span-2 xl:col-span-1' : ''}`}
                >
                  <ResourceLink
                    title={item.title}
                    text={item.text}
                    href={resourceLinks[item.key]}
                    icon={resourceIcons[item.key]}
                  />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 pt-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-6xl rounded-[32px] border border-cyan-300/15 bg-cyan-300/[0.06] p-8 text-center sm:p-10">
            <h2 className="text-4xl font-black text-white sm:text-5xl" style={{ fontFamily: displayFont }}>
              {copy.cta.title}
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-300">{copy.cta.text}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <LandingActionLink
                href={appHref}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-7 py-3.5 text-sm font-semibold text-cyan-50 transition-colors hover:border-cyan-300/40 hover:bg-cyan-300/15 sm:w-auto"
              >
                {appLabel}
                <ArrowRight className="h-4 w-4" />
              </LandingActionLink>
              <LandingActionLink
                href={landingLinks.guide}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-100 transition-colors hover:border-white/20 hover:text-white sm:w-auto"
              >
                <BookOpenText className="h-4 w-4" />
                {copy.cta.secondary}
              </LandingActionLink>
            </div>
          </Reveal>
        </section>
      </main>

      <LandingFooter copy={copy} />
    </div>
  );
}
